import { Button } from '@components/Button'
import { Dialog, Stack, Text, XStack, YStack, isWeb } from 'tamagui'

import { Modal } from '@components/Modal'
import { Trans, useTranslation } from 'react-i18next'
import { HeroIcon } from '@components/HeroIcon'
import { IFormattedDownsell } from '@queries/downsell'

type DownsellOfferModalProps = {
  open: boolean
  downsell: IFormattedDownsell | null
  accepting: boolean
  accepted: boolean
  hasError: boolean
  onAccept: () => void
  onDecline: () => void
  onClose: () => void
  courseName?: string
  cardMaskedNumber?: string
}

// Sufixo de periodicidade do preço ("R$ 50,00/mês"). Período desconhecido ou
// oferta unitária: sem sufixo.
const PERIOD_SUFFIX: Record<string, string> = {
  weekly: '/semana',
  monthly: '/mês',
  bimonthly: '/bimestre',
  quarterly: '/trimestre',
  semiannual: '/semestre',
  yearly: '/ano',
  annual: '/ano',
}

// Modal one-shot de retenção (layout do design de produto). Regras:
// - fechar (X/overlay) NÃO queima a oferta e NÃO cancela — só onDecline queima;
// - o aceite NUNCA cobra na hora: o valor novo vale a partir da próxima
//   cobrança (comunicado com data);
// - erro no aceite mantém o modal com retry — jamais cai no cancelamento.
export function DownsellOfferModal({
  open,
  downsell,
  accepting,
  accepted,
  hasError,
  onAccept,
  onDecline,
  onClose,
  courseName,
  cardMaskedNumber,
}: DownsellOfferModalProps) {
  const { t } = useTranslation('OrderDetails')

  if (!open && isWeb) return false
  if (!downsell) return false

  const periodSuffix = downsell.period
    ? (PERIOD_SUFFIX[downsell.period] ?? '')
    : ''
  const title =
    downsell.discountPercent === 50
      ? t('subscription.downsellModal.titleHalf')
      : t('subscription.downsellModal.title', {
          percent: downsell.discountPercent,
        })
  // Últimos 4 dígitos do cartão ("**** **** **** 4644" -> "4644").
  const cardLastDigits = cardMaskedNumber?.trim().split(' ').pop() ?? ''

  return (
    // backdrop mais forte que o padrão (0.1): no tema escuro o fundo do modal é
    // quase a cor da tela, e sem escurecer o resto ele some no fundo.
    <Modal
      onClose={onClose}
      open={open}
      snapPointsMode="fit"
      maxWidth={520}
      backdropOpacity={0.85}
    >
      <YStack
        gap="$size.spacing-xs"
        paddingHorizontal="$size.spacing-lg"
        paddingVertical="$size.spacing-xsl"
        $gtSm={{ p: '$size.spacing-lg' }}
      >
        {accepted ? (
          <>
            <HeroIcon
              icon={['fal', 'circle-check']}
              color="$success-300"
              size={48}
            />
            <YStack gap="$size.spacing-xxs">
              <Dialog.Title
                color="$text-primary"
                fontFamily="$mulishBold"
                fontSize="$font-size-sm"
              >
                {t('subscription.downsellModal.accepted.title')}
              </Dialog.Title>
              <Dialog.Description
                color="$text-primary"
                fontFamily="$mulishRegular"
                fontSize="$font-size-xxs"
              >
                <Trans
                  t={t}
                  i18nKey="subscription.downsellModal.accepted.description"
                  values={{ courseName }}
                  components={{ bold: <Text ff="$mulishBold" /> }}
                />
              </Dialog.Description>
            </YStack>

            <YStack
              borderWidth="$borderWidth.border-width-hairline"
              borderColor="$border-strong"
              br="$border-radius-sm"
              testID="downsell-accepted-summary"
            >
              <XStack
                jc="space-between"
                ai="center"
                p="$size.spacing-xs"
                borderBottomWidth="$borderWidth.border-width-hairline"
                borderColor="$border-strong"
              >
                <Text
                  color="$text-secondary"
                  ff="$mulishRegular"
                  fontSize="$font-size-xxs"
                >
                  {t('subscription.downsellModal.accepted.newAmountLabel')}
                </Text>
                <XStack gap="$size.spacing-xxxs" ai="center">
                  <Text
                    color="$text-muted"
                    ff="$mulishRegular"
                    fontSize="$font-size-xxs"
                    textDecorationLine="line-through"
                  >
                    {downsell.currentAmount}
                  </Text>
                  <Text
                    color="$accent"
                    ff="$mulishBold"
                    fontWeight="700"
                    fontSize="$font-size-xs"
                  >
                    {downsell.newAmount}
                  </Text>
                </XStack>
              </XStack>
              <XStack
                jc="space-between"
                ai="center"
                p="$size.spacing-xs"
                borderBottomWidth={
                  cardLastDigits ? '$borderWidth.border-width-hairline' : 0
                }
                borderColor="$border-strong"
              >
                <Text
                  color="$text-secondary"
                  ff="$mulishRegular"
                  fontSize="$font-size-xxs"
                >
                  {t('subscription.downsellModal.accepted.nextChargeLabel')}
                </Text>
                <Text
                  color="$text-primary"
                  ff="$mulishBold"
                  fontWeight="700"
                  fontSize="$font-size-xxs"
                >
                  {downsell.startsAt}
                </Text>
              </XStack>
              {Boolean(cardLastDigits) && (
                <XStack jc="space-between" ai="center" p="$size.spacing-xs">
                  <Text
                    color="$text-secondary"
                    ff="$mulishRegular"
                    fontSize="$font-size-xxs"
                  >
                    {t(
                      'subscription.downsellModal.accepted.paymentMethodLabel',
                    )}
                  </Text>
                  <XStack gap="$size.spacing-xxxs" ai="center">
                    <HeroIcon
                      icon={['fal', 'credit-card']}
                      color="$icon-secondary"
                      size={16}
                    />
                    <Text
                      color="$text-primary"
                      ff="$mulishBold"
                      fontWeight="700"
                      fontSize="$font-size-xxs"
                    >
                      •••• {cardLastDigits}
                    </Text>
                  </XStack>
                </XStack>
              )}
            </YStack>

            <XStack gap="$size.spacing-xxxs" ai="center">
              <HeroIcon
                icon={['fal', 'circle-info']}
                color="$icon-secondary"
                size={16}
              />
              <Text
                color="$text-secondary"
                ff="$mulishRegular"
                fontSize="$font-size-xxs"
                flex={1}
              >
                {t('subscription.downsellModal.accepted.note')}
              </Text>
            </XStack>

            <Button
              variant="primary"
              aria-label="Understood"
              onPress={onClose}
              width="100%"
            >
              {t('subscription.downsellModal.accepted.cta')}
            </Button>
          </>
        ) : (
          <>
            {/* Cores literais do design (não tokens do tema): pill translúcida
                carmim com texto rosa — igual em qualquer tema de escola. */}
            <XStack
              alignSelf="flex-start"
              ai="center"
              gap="$size.spacing-xxxs"
              backgroundColor="rgba(244, 63, 94, 0.16)"
              br="$border-radius-pill"
              paddingHorizontal="$size.spacing-xxs"
              paddingVertical="$size.spacing-xxxs"
              testID="downsell-one-shot-badge"
            >
              <HeroIcon icon={['fal', 'clock']} color="#FB7185" size={14} />
              <Text color="#FB7185" ff="$mulishBold" fontSize="$font-size-xxxs">
                {t('subscription.downsellModal.oneShotBadge')}
              </Text>
            </XStack>

            <YStack gap="$size.spacing-xxs">
              <Dialog.Title
                color="$text-primary"
                fontFamily="$mulishBold"
                fontSize="$font-size-md"
              >
                {title}
              </Dialog.Title>
              <Dialog.Description
                color="$text-primary"
                fontFamily="$mulishRegular"
                fontSize="$font-size-xxs"
              >
                <Trans
                  t={t}
                  i18nKey="subscription.downsellModal.description"
                  values={{ courseName }}
                  components={{ bold: <Text ff="$mulishBold" /> }}
                />
              </Dialog.Description>
            </YStack>

            <XStack
              ai="center"
              jc="space-between"
              gap="$size.spacing-xs"
              p="$size.spacing-xs"
              borderWidth="$borderWidth.border-width-hairline"
              borderColor="$accent"
              br="$border-radius-sm"
              flexWrap="wrap"
              testID="downsell-price-card"
            >
              <YStack gap="$size.spacing-nano">
                <Text
                  color="$text-muted"
                  ff="$mulishBold"
                  fontWeight="700"
                  fontSize="$font-size-xxxs"
                  textTransform="uppercase"
                >
                  {t('subscription.downsellModal.todayLabel')}
                </Text>
                <Text
                  color="$text-muted"
                  ff="$mulishBold"
                  fontWeight="700"
                  fontSize="$font-size-xs"
                  textDecorationLine="line-through"
                  testID="downsell-current-price"
                >
                  {downsell.currentAmount}
                </Text>
              </YStack>
              <HeroIcon
                icon={['fal', 'arrow-right']}
                color="$text-muted"
                size={16}
              />
              <YStack gap="$size.spacing-nano" flex={1}>
                <Text
                  color="$accent"
                  ff="$mulishBold"
                  fontWeight="700"
                  fontSize="$font-size-xxxs"
                  textTransform="uppercase"
                >
                  {t('subscription.downsellModal.newAmountLabel')}
                </Text>
                <XStack ai="flex-end" gap="$size.spacing-nano">
                  <Text
                    color="$accent"
                    ff="$mulishBold"
                    fontWeight="700"
                    fontSize="$font-size-md"
                    testID="downsell-new-price"
                  >
                    {downsell.newAmount}
                  </Text>
                  {Boolean(periodSuffix) && (
                    <Text
                      color="$text-primary"
                      ff="$mulishBold"
                      fontWeight="700"
                      fontSize="$font-size-xxs"
                    >
                      {periodSuffix}
                    </Text>
                  )}
                </XStack>
              </YStack>
              {downsell.discountPercent > 0 && (
                <Stack
                  backgroundColor="#F43F5E"
                  br="$border-radius-pill"
                  paddingHorizontal="$size.spacing-xxs"
                  paddingVertical="$size.spacing-nano"
                  testID="downsell-discount-badge"
                >
                  <Text
                    color="#FFFFFF"
                    ff="$mulishBold"
                    fontWeight="700"
                    fontSize="$font-size-xxs"
                  >
                    -{downsell.discountPercent}%
                  </Text>
                </Stack>
              )}
            </XStack>

            {downsell.startsAt && (
              <XStack gap="$size.spacing-xxs" ai="flex-start">
                <HeroIcon
                  icon={['fal', 'calendar-alt']}
                  color="$accent"
                  size={18}
                />
                <Text
                  color="$text-primary"
                  ff="$mulishRegular"
                  fontSize="$font-size-xxs"
                  flex={1}
                >
                  <Trans
                    t={t}
                    i18nKey="subscription.downsellModal.startsAt"
                    values={{ date: downsell.startsAt }}
                    components={{ bold: <Text ff="$mulishBold" /> }}
                  />
                </Text>
              </XStack>
            )}
            <XStack gap="$size.spacing-xxs" ai="flex-start">
              <HeroIcon icon={['fal', 'check']} color="$accent" size={18} />
              <Text
                color="$text-primary"
                ff="$mulishRegular"
                fontSize="$font-size-xxs"
                flex={1}
              >
                {t('subscription.downsellModal.sameAccess')}
              </Text>
            </XStack>

            {hasError && (
              <Text
                color="$danger-300"
                ff="$mulishRegular"
                fontSize="$font-size-xxs"
                testID="downsell-error"
              >
                {t('subscription.downsellModal.error.description')}
              </Text>
            )}

            <Stack
              mt="$size.spacing-xxs"
              alignItems="center"
              flexDirection="column"
              width="100%"
              gap="$size.spacing-xxs"
            >
              {/* focusStyle sólido: o Dialog auto-foca o primeiro botão ao
                  abrir e o focusStyle padrão ($accent-pressed) deixava o CTA
                  "apagado" antes de qualquer interação. */}
              <Button
                variant="primary"
                aria-label="Accept downsell"
                loading={accepting}
                onPress={onAccept}
                width="100%"
                focusStyle={{ bg: '$accent' }}
              >
                {hasError
                  ? t('subscription.downsellModal.error.retry')
                  : t('subscription.downsellModal.accept', {
                      amount: `${downsell.newAmount}${periodSuffix}`,
                    })}
              </Button>
              <Button
                variant="secondary"
                link
                aria-label="Decline downsell"
                disabled={accepting}
                onPress={onDecline}
                width="100%"
              >
                <Text
                  color="$text-muted"
                  ff="$mulishBold"
                  fontSize="$font-size-xxs"
                  textDecorationLine="underline"
                >
                  {t('subscription.downsellModal.decline')}
                </Text>
              </Button>
            </Stack>

            <Text
              color="$text-muted"
              ff="$mulishRegular"
              fontSize="$font-size-xxxs"
              textAlign="center"
              testID="downsell-one-shot-warning"
            >
              {t('subscription.downsellModal.oneShot')}
            </Text>
          </>
        )}
      </YStack>
    </Modal>
  )
}
